/**
 * FeedbackPage
 *
 * Web port of Flutter's `InAppFeedbackScreen`
 * (`lib/presentation/screens/feedback/in_app_feedback_screen.dart:244-509`): an
 * app bar ("Feedback" + back arrow), a disclaimer, a 200-char feedback input, a
 * 3-cell screenshots picker grid (up to 3 images), and a "Done" CTA. The page
 * composes everything and owns all presentation surfaces (sheets + navigation);
 * `useFeedback` owns the draft state + submit logic.
 *
 * Submit ordering is **parity-load-bearing** (`_submitFeedback`, `:201-242`):
 *   1. Show the confirm sheet — its Send button shows NO loading (Pattern 1: no
 *      `onConfirm`, so the sheet just resolves + closes on tap, `:214-218`).
 *   2. Only after the sheet closes does `isSubmitting` flip → the PAGE renders a
 *      full-screen loading overlay during the save (`:219-230`).
 *   3. On completion the page navigates back **unconditionally** (mirroring
 *      Flutter's pop-regardless-of-`saved`, `:231-240`). Cancel → stay on page.
 *
 * Back is blocked while submitting, mirroring `_onBackPress` (`:60-65`).
 *
 * App-bar provenance: the bar is the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`). Flutter builds
 * this screen's bar as a **raw inline `AppBar`** (`:250-265`) rather than via the
 * shared `BaseAppBar` widget, but its shape is the same one `ExpauseAppBar`
 * ports (`lib/presentation/custom_widgets/base_app_bar.dart:5-42`: back leading +
 * `centerTitle: true` + `kToolbarHeight`), so the swap is a like-for-like
 * structural match, not a new affordance.
 *
 * Moving from the previously co-located one-off feedback bar (deleted on this
 * branch) to `ExpauseAppBar` makes three intentional visual changes: the title
 * steps from `responsive.font.lg`/`semibold` to `responsive.font.xl`/`bold`, the
 * bar becomes sticky with a blurred 50%-opacity backdrop, and the top safe-area
 * inset is now honoured. The submit guard on back (`handleBack`) is unchanged —
 * that guard is Flutter behaviour, not styling; only the pop underneath it moved
 * to the deep-link-guarded `Navigator.goBackOr`.
 *
 * The two-part body lives in `./components/FeedbackForm` to keep this file under
 * the ~200-line Should-Fix threshold from `presentation.md`. UI conventions
 * (theme tokens, responsive sizing, sheets-not-dialogs, an overlaid indicator
 * instead of Flutter's whole-body swap) deviate from Flutter per `presentation.md`.
 */
import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { AddContentSheet } from '@/presentation/feature/publish/components/sheets/addContent/AddContentSheet';
import { LocalMediaSheet } from '@/presentation/components/common/sheet/localMedia/LocalMediaSheet';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { MAX_FEEDBACK_SCREENSHOTS } from '@/domain/constants/feedbackConstants';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { useFeedback } from './hooks/useFeedback';
import { FeedbackForm } from './components/FeedbackForm';
import { FeedbackSubmitOverlay } from './components/FeedbackSubmitOverlay';

export const FeedbackPage = () => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const location = useLocation();

  // Canonical screen-key wiring (mirrors `AddRoadmapPage` / `ContactPage`).
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  useBasePage({ screenKey, onInit: () => {} });

  // Auth gate — Flutter stamps the doc with `firebaseUser.uid`
  // (feedback_repository.dart:17); the hook short-circuits `submitFeedback` to
  // `false` on a falsy id.
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const {
    description,
    setDescription,
    screenshots,
    addScreenshot,
    removeScreenshot,
    isSubmitting,
    canSubmit,
    submitFeedback,
  } = useFeedback(currentUser?.firebaseId);

  // Guarded back — Flutter's `_onBackPress` pops only while `!_isSubmitting`
  // (`:60-65`). The submit guard is Flutter behaviour and comes first; the pop
  // itself is then the deep-link-guarded `goBackOr` (`presentation.md` → App bar
  // → "Back affordance"), a superset of Flutter's `NavigationService.pop()`.
  // Fallback is the Feed: the only in-app entry is the home top-bar menu
  // (`HomeTopBarBody.tsx`), so the home shell IS this route's parent.
  const handleBack = useCallback(() => {
    if (isSubmitting) return;
    Navigator.goBackOr(() => Navigator.navigateToFeed());
  }, [isSubmitting]);

  // Open the feedback `AddContentSheet` (image-only, collect-not-send). The
  // Preview "Done" hands the picked image back via `onPickImage`, which appends
  // it to the draft list (`useFeedback.addScreenshot`). Guarded to skip once 3
  // screenshots are present, parity with `_onUploadTapped` (`:127-129`).
  const handleAddTap = useCallback(() => {
    if (screenshots.length >= MAX_FEEDBACK_SCREENSHOTS) return;
    AddContentSheet.show({
      screenKey,
      width: responsive.addContentSheet.width,
      fixedHeight: responsive.addContentSheet.fixedHeight,
      feedback: { onPickImage: addScreenshot },
    });
  }, [screenshots.length, screenKey, responsive, addScreenshot]);

  // Open the local-image viewer for a picked screenshot — the web replacement
  // for `_onScreenshotTapped` (`:83-111`). Bounds-checked (parity `:84-86`).
  const handleScreenshotTap = useCallback(
    (index: number) => {
      const shot = screenshots[index];
      if (!shot) return;
      LocalMediaSheet.show({
        file: shot.file,
        aspectRatio: shot.aspectRatio,
        // Title + Close label resolved here (caller has `l10n`) and forwarded to
        // the sheet's native `title` / `cta` slots.
        title: l10n.feedback.screenshotViewerTitle,
        closeLabel: l10n.common.close,
        screenKey,
        fixedHeight: responsive.addFeedback.sheetFixedHeight,
      });
    },
    [screenshots, screenKey, responsive, l10n],
  );

  // Submit ordering (parity-load-bearing, `_submitFeedback` `:201-242`): confirm
  // (no button loading) → page overlay via `isSubmitting` → save → pop
  // unconditionally.
  const handleDone = useCallback(async () => {
    if (!canSubmit) return;
    const response = await ConfirmSheet.show({
      title: l10n.feedback.confirmTitle,
      confirmText: l10n.feedback.confirmDescription,
      okText: l10n.feedback.confirmSend,
      cancelText: l10n.common.cancel,
      screenKey,
    });
    // Cancel / dismiss → stay on the page (`:218` guards on `confirmed`).
    if (!response) return;
    await submitFeedback();
    // Pop regardless of the saved result (`:239-240`) — same deep-link guard as
    // `handleBack`, so a visitor who deep-linked to `/feedback` and submitted
    // isn't left staring at the form they just sent.
    Navigator.goBackOr(() => Navigator.navigateToFeed());
  }, [canSubmit, submitFeedback, l10n, screenKey]);

  return (
    <div
      {...qaAttr({
        id: 'feedback-add',
        status: isSubmitting ? 'submitting' : 'ready',
      })}
      style={{
        position: 'relative',
        minHeight: '100vh',
        backgroundColor: theme.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* App bar — back button + centered title (`:250-265`). `leadingQa` keeps
          the `feedback-back` id that used to be set inside the deleted one-off
          feedback bar component. */}
      <ExpauseAppBar
        rootQa={{ id: 'feedback-app-bar' }}
        title={l10n.feedback.title}
        leadingQa={{ id: 'feedback-back' }}
        onLeadingTap={handleBack}
      />

      {/* Body — disclaimer + input + grid + Done. */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: `${responsive.spacing.lg}px`,
        }}
      >
        <div style={{ width: '100%' }}>
          <FeedbackForm
            description={description}
            setDescription={setDescription}
            screenshots={screenshots}
            canSubmit={canSubmit}
            onAddTap={handleAddTap}
            onScreenshotTap={handleScreenshotTap}
            onRemoveTap={removeScreenshot}
            onDone={handleDone}
          />
        </div>
      </div>

      {/* Loading overlay while submitting — blocks interaction + back, parity
          with Flutter's `_isSubmitting` layer (`:266-280`). */}
      {isSubmitting && <FeedbackSubmitOverlay />}
    </div>
  );
};
